// ── Admin RBAC + Audit — единый контракт админ-панели v2 ──────────────────
//
// Single source of truth для набора административных прав. И сервер (проверка
// доступа), и клиент (гейтинг навигации/кнопок) импортируют ОТСЮДА. Добавление
// нового права — правка только этого файла: строку в ADMIN_PERMISSIONS и (если
// нужно) в дефолтные наборы системных ролей ниже.
/** Плоский каталог пермишенов вида `<домен>.<действие>`. */
export const ADMIN_PERMISSIONS = [
    'analytics.view',
    'users.view',
    'users.moderate',
    'users.delete',
    'rooms.view',
    'rooms.manage',
    'rooms.delete',
    'moderation.dm.view',
    'reports.view',
    'reports.handle',
    'push.view',
    'push.send',
    'push.templates',
    'media.manage',
    'system.view',
    'jobs.manage',
    'platform.manage',
    'flags.manage',
    'announcements.manage',
    'roles.manage',
    'audit.view',
    'broadcast.send',
];
/** Человекочитаемые названия прав (для UI редактора ролей). */
export const ADMIN_PERMISSION_LABELS = {
    'analytics.view': 'Аналитика — просмотр',
    'users.view': 'Пользователи — просмотр',
    'users.moderate': 'Пользователи — модерация (блок, сброс, сессии)',
    'users.delete': 'Пользователи — удаление',
    'rooms.view': 'Комнаты — просмотр',
    'rooms.manage': 'Комнаты — управление (правка, закрыть, звонок, вход)',
    'rooms.delete': 'Комнаты — удаление',
    'moderation.dm.view': 'Модерация ЛС — просмотр переписки (чувствительно)',
    'reports.view': 'Жалобы — просмотр очереди',
    'reports.handle': 'Жалобы — решения',
    'push.view': 'Push — дашборд и аналитика',
    'push.send': 'Push — рассылки',
    'push.templates': 'Push — шаблоны',
    'media.manage': 'Медиа-кэш — управление',
    'system.view': 'Система — мониторинг (WS, производительность, health)',
    'jobs.manage': 'Фоновые задачи — управление',
    'platform.manage': 'Платформа — конфигурация и режим обслуживания',
    'flags.manage': 'Feature flags — управление',
    'announcements.manage': 'Объявления — управление',
    'roles.manage': 'Роли и доступ — управление',
    'audit.view': 'Журнал аудита — просмотр',
    'broadcast.send': 'Системное сообщение во все комнаты',
};
/** Группировка прав по доменам — для секций в редакторе ролей. */
export const ADMIN_PERMISSION_GROUPS = [
    { label: 'Аналитика', keys: ['analytics.view'] },
    { label: 'Пользователи', keys: ['users.view', 'users.moderate', 'users.delete'] },
    { label: 'Комнаты', keys: ['rooms.view', 'rooms.manage', 'rooms.delete'] },
    { label: 'Модерация', keys: ['moderation.dm.view', 'reports.view', 'reports.handle'] },
    { label: 'Push', keys: ['push.view', 'push.send', 'push.templates', 'broadcast.send'] },
    { label: 'Система', keys: ['system.view', 'jobs.manage', 'media.manage'] },
    { label: 'Платформа', keys: ['platform.manage', 'flags.manage', 'announcements.manage'] },
    { label: 'Доступ', keys: ['roles.manage', 'audit.view'] },
];
/**
 * Дефолтные наборы прав системных ролей. `super_admin` — специальный: имеет ВСЕ
 * права всегда (в т.ч. будущие), поэтому его набор вычисляется как весь каталог.
 * Остальные — редактируемы из UI, здесь лишь стартовый сид.
 */
export const SYSTEM_ROLE_DEFS = {
    super_admin: {
        name: 'Super Admin',
        description: 'Полный доступ, включая управление ролями. Неудаляем и непонижаем.',
        permissions: [...ADMIN_PERMISSIONS],
    },
    administrator: {
        name: 'Administrator',
        description: 'Полный доступ, кроме управления ролями.',
        permissions: ADMIN_PERMISSIONS.filter((p) => p !== 'roles.manage'),
    },
    moderator: {
        name: 'Moderator',
        description: 'Модерация пользователей, комнат и жалоб.',
        permissions: [
            'analytics.view',
            'users.view',
            'users.moderate',
            'rooms.view',
            'rooms.manage',
            'reports.view',
            'reports.handle',
            'moderation.dm.view',
            'audit.view',
        ],
    },
    support: {
        name: 'Support',
        description: 'Просмотр пользователей/комнат/жалоб и push-дашборда.',
        permissions: ['users.view', 'rooms.view', 'reports.view', 'push.view'],
    },
    analyst: {
        name: 'Analyst',
        description: 'Только чтение: аналитика и системный мониторинг.',
        permissions: ['analytics.view', 'system.view'],
    },
};
// ── Feature flags ────────────────────────────────────────────────────────────
/**
 * Well-known ключ feature-флага приёма жалоб. По умолчанию включён; когда
 * выключен в разделе «Feature flags» — весь функционал жалоб скрывается
 * полностью (кнопка в профиле, модалка, админ-раздел «Жалобы»), а сервер
 * отклоняет создание новых жалоб.
 */
export const FEATURE_FLAG_REPORTS = 'reports';
export const REPORT_REASON_LABELS = {
    spam: 'Спам',
    harassment: 'Оскорбления / травля',
    nsfw: 'Непристойный контент',
    illegal: 'Противозаконное',
    other: 'Другое',
};
//# sourceMappingURL=admin.js.map